;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React, { useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import cx from 'classnames';
import PropTypes from 'prop-types';

import Button from 'components/ui/Button';

import highlightText from 'utils/highLightText';

import Search from './Search';

import SvgIcon from '../SvgIcon';

import styles from './PlaceOfPurchase.module.scss';

const RETURN_KEYCODE = 13;
const ESCAPE_KEYCODE = 27;
const ARROW_UP_KEYCODE = 38;
const ARROW_DOWN_KEYCODE = 40;
const TAB_KEYCODE = 9;
const NUMBER_OF_SUGGESTIONS = 3;

const matchStores = (str, items) => {
    if (str.length === 0) return [];

    // Normalize text by removing punctuation, extra spaces, and converting to lowercase
    const normalizeText = text => {
        return text
            .toLowerCase()
            .replace(/[^\w\s]/g, '') // Remove punctuation
            .replace(/\s+/g, ' ') // Normalize spaces
            .trim();
    };

    const normalizedQuery = normalizeText(str);
    const queryWords = normalizedQuery
        .split(' ')
        .filter(word => word.length > 0);

    const results = items
        .map(name => {
            const normalizedName = normalizeText(name);
            let score = 0;

            // Exact match (highest priority)
            if (normalizedName === normalizedQuery) {
                score = 1000;
            }
            // Starts with query (high priority)
            else if (normalizedName.startsWith(normalizedQuery)) {
                score = 900;
            }
            // Contains the full query (medium-high priority)
            else if (normalizedName.includes(normalizedQuery)) {
                score = 800;
            }
            // Word-based matching (medium priority)
            else {
                const nameWords = normalizedName.split(' ');
                let matchedWords = 0;
                let partialMatches = 0;

                queryWords.forEach(queryWord => {
                    // Check for exact word matches
                    if (nameWords.some(nameWord => nameWord === queryWord)) {
                        matchedWords++;
                    }
                    // Check for partial word matches (word starts with query word)
                    else if (
                        nameWords.some(nameWord =>
                            nameWord.startsWith(queryWord)
                        )
                    ) {
                        partialMatches++;
                    }
                });

                // Calculate score based on word matches
                if (matchedWords > 0 || partialMatches > 0) {
                    score = matchedWords * 100 + partialMatches * 50;

                    // Bonus for matching all query words
                    if (matchedWords + partialMatches === queryWords.length) {
                        score += 200;
                    }
                }
            }

            return { name, score };
        })
        .filter(item => item.score > 0) // Only include items with matches
        .sort((a, b) => b.score - a.score) // Sort by score (highest first)
        .map(item => item.name); // Extract just the names

    return results;
};

const PlaceOfPurchase = ({
    register,
    retailers,
    fieldName,
    displaySuggestions = true,
    watch,
    setValue,
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [bestMatches, setBestMatches] = useState([]);
    const [selectedValue, setSelectedValue] = useState('');
    const [collapsed, setCollapsed] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [allOptions, setAllOptions] = useState([]);
    const suggestionsListboxId = `${fieldName}-suggestions`;

    const closeSuggestions = () => {
        setShowDropdown(false);
        setBestMatches([]);
        setSearchValue('');
    };

    const clearSelection = () => {
        setSelectedValue('');
        setValue(fieldName, '');
        setSearchValue('');
        setBestMatches([]);
        setCollapsed(true);
        setShowDropdown(false);
    };

    const selectOption = option => {
        setSelectedValue(option);
        setValue(fieldName, option);
        setSearchValue('');
        closeSuggestions();
        setCollapsed(true);
    };

    const handleInputKeyDown = e => {
        const totalOptions = allOptions.length;

        switch (e.keyCode) {
            case RETURN_KEYCODE:
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < totalOptions) {
                    // Select the focused option
                    selectOption(allOptions[focusedIndex]);
                } else if (bestMatches.length > 0) {
                    // If there's a match, select the first one
                    selectOption(bestMatches[0]);
                } else if (searchValue.trim()) {
                    // If no matches but there's text, select "Other"
                    selectOption('Other');
                }
                break;

            case ARROW_DOWN_KEYCODE:
                e.preventDefault();
                if (totalOptions > 0) {
                    setFocusedIndex(prev =>
                        prev < totalOptions - 1 ? prev + 1 : 0
                    );
                }
                break;

            case ARROW_UP_KEYCODE:
                e.preventDefault();
                if (totalOptions > 0) {
                    setFocusedIndex(prev =>
                        prev > 0 ? prev - 1 : totalOptions - 1
                    );
                }
                break;

            case ESCAPE_KEYCODE:
                e.preventDefault();
                closeSuggestions();
                setFocusedIndex(-1);
                break;

            case TAB_KEYCODE:
                // Allow normal tab behavior but close suggestions
                closeSuggestions();
                setFocusedIndex(-1);
                break;
        }
    };

    const handleInputClick = () => {
        if (selectedValue) {
            // If there's a selected value, clear it to allow new selection
            clearSelection();
        }
    };

    useEffect(() => {
        if (searchValue.length > 0) {
            const matches = matchStores(searchValue, retailers);
            setBestMatches(matches.slice(0, NUMBER_OF_SUGGESTIONS));
            setCollapsed(false);

            // Build all options array for keyboard navigation
            const options = [...matches.slice(0, NUMBER_OF_SUGGESTIONS)];
            if (searchValue && matches.length === 0) {
                options.push('Other');
            } else if (matches.length > 0) {
                options.push('Other');
            }
            setAllOptions(options);
            setFocusedIndex(-1); // Reset focus when options change
        } else {
            setBestMatches([]);
            setCollapsed(true);
            setAllOptions([]);
            setFocusedIndex(-1);
        }
    }, [searchValue, retailers]);

    watch(fieldName);

    const fieldRef = useRef(null);
    const wrapperRef = useRef(null);

    const { ref, onChange, ...rest } = register(fieldName, {
        required: false,
    });

    useClickAway(wrapperRef, () => {
        closeSuggestions();
        setCollapsed(true);
    });

    return (
        <div className={styles.root} ref={wrapperRef}>
            <div
                className={cx(styles.innerWrapper, {
                    [styles.collapsed]: collapsed,
                })}
            >
                <div className={styles.inputWrapper}>
                    <input
                        {...rest}
                        ref={e => {
                            ref(e);
                            fieldRef.current = e;
                        }}
                        title={
                            selectedValue
                                ? 'Click to change retailer'
                                : 'Type to find a retailer'
                        }
                        placeholder={selectedValue || 'Type to find a retailer'}
                        autoComplete="off"
                        type="text"
                        value={selectedValue || searchValue}
                        readOnly={!!selectedValue}
                        onChange={e => {
                            if (!selectedValue) {
                                const value =
                                    e.target.value?.charAt(0).toUpperCase() +
                                    e.target.value?.substring(1);
                                setSearchValue(value);
                                onChange(e);
                            }
                        }}
                        onKeyDown={handleInputKeyDown}
                        onClick={handleInputClick}
                        className={cx(styles.input, {
                            [styles.selected]: !!selectedValue,
                            [styles.readonly]: !!selectedValue,
                        })}
                        enterKeyHint="search"
                        role="combobox"
                        aria-expanded={!collapsed && allOptions.length > 0}
                        aria-haspopup="listbox"
                        aria-controls={suggestionsListboxId}
                        aria-activedescendant={
                            focusedIndex >= 0
                                ? `${fieldName}-option-${focusedIndex}`
                                : undefined
                        }
                        aria-describedby={`${fieldName}-instructions`}
                    />
                    {selectedValue && (
                        <div className={styles.buttonContainer}>
                            <Button
                                theme="close"
                                onClick={clearSelection}
                                className={styles.clearButton}
                                aria-label="Clear retailer selection"
                                type="button"
                            >
                                <SvgIcon
                                    type="close"
                                    title="Clear retailer selection"
                                />
                            </Button>
                        </div>
                    )}
                </div>
                {displaySuggestions && (
                    <div
                        className={cx(styles.innerWrapper, {
                            [styles.collapsed]: collapsed,
                        })}
                    >
                        <div
                            id={suggestionsListboxId}
                            className={styles.suggestionsWrapper}
                            role="listbox"
                            aria-label="Retailer suggestions"
                        >
                            {bestMatches.map((store, index) => (
                                <div
                                    key={`${store}-${index}`}
                                    id={`${fieldName}-option-${index}`}
                                    className={cx(styles.suggestion, {
                                        [styles.focused]:
                                            focusedIndex === index,
                                    })}
                                    onClick={() => selectOption(store)}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                    onMouseLeave={() => setFocusedIndex(-1)}
                                    role="option"
                                    aria-selected={focusedIndex === index}
                                    tabIndex={-1}
                                    dangerouslySetInnerHTML={{
                                        __html: highlightText(
                                            store,
                                            searchValue,
                                            'em'
                                        ),
                                    }}
                                />
                            ))}
                            {/* Show "Other" option if no matches found and user is searching */}
                            {searchValue && bestMatches.length === 0 && (
                                <div
                                    id={`${fieldName}-option-${bestMatches.length}`}
                                    className={cx(
                                        styles.suggestion,
                                        styles.otherOption,
                                        {
                                            [styles.focused]:
                                                focusedIndex ===
                                                bestMatches.length,
                                        }
                                    )}
                                    onClick={() => selectOption('Other')}
                                    onMouseEnter={() =>
                                        setFocusedIndex(bestMatches.length)
                                    }
                                    onMouseLeave={() => setFocusedIndex(-1)}
                                    role="option"
                                    aria-selected={
                                        focusedIndex === bestMatches.length
                                    }
                                    tabIndex={-1}
                                >
                                    Other
                                </div>
                            )}
                            {/* Show "Other" option at the end if there are matches */}
                            {bestMatches.length > 0 && (
                                <div
                                    id={`${fieldName}-option-${bestMatches.length}`}
                                    className={cx(
                                        styles.suggestion,
                                        styles.otherOption,
                                        {
                                            [styles.focused]:
                                                focusedIndex ===
                                                bestMatches.length,
                                        }
                                    )}
                                    onClick={() => selectOption('Other')}
                                    onMouseEnter={() =>
                                        setFocusedIndex(bestMatches.length)
                                    }
                                    onMouseLeave={() => setFocusedIndex(-1)}
                                    role="option"
                                    aria-selected={
                                        focusedIndex === bestMatches.length
                                    }
                                    tabIndex={-1}
                                >
                                    Other
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {/* Screen reader instructions */}
                <div id={`${fieldName}-instructions`} className="sr-only">
                    Use arrow keys to navigate suggestions, Enter to select,
                    Escape to close
                </div>
            </div>
        </div>
    );
};

PlaceOfPurchase.propTypes = {
    register: PropTypes.func.isRequired,
    retailers: PropTypes.array.isRequired,
    fieldName: PropTypes.string.isRequired,
    displaySuggestions: PropTypes.bool,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
};

export default PlaceOfPurchase;
